// src/components/tabs/PatientsStats.tsx
import React from "react";
import "../../style/css/PatientGrid.css";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import Error403Page from "../Error_backend";

/**
 * Types expected from backend
 */
interface AgeBucket {
  ageGroup: string;
  count: number;
}

interface TopActivePatient {
  patientName: string;
  appointmentCount: number;
  patientID?: string;
}

interface PatientsData {
  totalPatientsInPeriod: number;
  ageDistribution: AgeBucket[];
  patientActivityMetrics: {
    topActivePatients: TopActivePatient[];
    firstTimeVsRecurrent: { firstTime: number; recurrent: number };
  };
}

interface TabComponentProps {
  data: PatientsData | null;
  loading: boolean;
  error: string | null;
  dateRange: { startDate: string; endDate: string };
  onRetry?: () => void;
}

const AGE_COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];
const PIE_COLORS = ["#8884d8", "#00C49F", "#FF8042", "#FFBB28"];

const PatientsStats: React.FC<TabComponentProps> = ({
  data,
  loading,
  error,
  dateRange,
  onRetry,
}) => {
  // Styles specifically requested for "Pacientes Más Activos"
  const sectionStyle: React.CSSProperties = {
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
    flexWrap: "wrap",
  };

  const topActiveListStyle: React.CSSProperties = {
    background: "var(--neutral-100, #ffffff)",
    padding: 12,
    borderRadius: 10,
    boxShadow: "0 6px 18px rgba(20,20,40,0.04)",
    minWidth: 280,
    maxWidth: 420,
    maxHeight: 280,
    overflowY: "auto",
  };

  // Render error 403 page (custom) if backend denies access
  if (error) {
    return <Error403Page />;
  }

  if (loading) {
    return (
      <div className="stats-dashboard-header">
        <div className="loading-state">
          Cargando estadísticas de pacientes...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stats-dashboard-header">
        <div className="error-state">
          Error: {error}
          {onRetry && (
            <button onClick={onRetry} style={{ marginLeft: "10px" }}>
              Reintentar
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="stats-dashboard-header">
        <div className="empty-state">
          No hay datos disponibles para el período seleccionado.
        </div>
      </div>
    );
  }

  const {
    totalPatientsInPeriod,
    ageDistribution,
    patientActivityMetrics: { topActivePatients, firstTimeVsRecurrent },
  } = data;

  const ageData = ageDistribution.map((a) => ({
    name: a.ageGroup,
    value: a.count,
  }));
  const aux = firstTimeVsRecurrent.recurrent + firstTimeVsRecurrent.firstTime;
  const firstRecurrentData = [
    { name: "Primera vez", value: firstTimeVsRecurrent.firstTime },
    { name: "Recurrentes", value: firstTimeVsRecurrent.recurrent },
    { name: "Sin cita", value: aux },
  ];
  const without_appointment = totalPatientsInPeriod - aux;
  const totalPatients = totalPatientsInPeriod;

  return (
    <div className="stats-dashboard-header">
      <div className="metrics-summary">
        <div className="metric-card">
          <h3>Total Pacientes</h3>
          <div className="metric-value">{totalPatientsInPeriod}</div>
        </div>
        <div className="metric-card">
          <h3>Pacientes Nuevos</h3>
          <div className="metric-value">
            {firstTimeVsRecurrent.firstTime} (
            {totalPatients > 0
              ? (
                  (firstTimeVsRecurrent.firstTime / totalPatients) *
                  100
                ).toFixed(1)
              : 0}
            %)
          </div>
        </div>
        <div className="metric-card">
          <h3>Pacientes Recurrentes</h3>
          <div className="metric-value">
            {firstTimeVsRecurrent.recurrent} (
            {totalPatients > 0
              ? (
                  (firstTimeVsRecurrent.recurrent / totalPatients) *
                  100
                ).toFixed(1)
              : 0}
            %)
          </div>
        </div>
        <div className="metric-card">
          <h3>Pacientes Sin cita</h3>
          <div className="metric-value">
            {without_appointment} (
            {totalPatients > 0
              ? ((without_appointment / totalPatients) * 100).toFixed(1)
              : 0}
            %)
          </div>
        </div>
      </div>
      <div className="stats-section">
        <h3>Distribución por Edad</h3>
        <div className="charts-grid">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  dataKey="value"
                  data={ageData}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {ageData.map((entry, idx) => (
                    <Cell
                      key={`age-${idx}`}
                      fill={AGE_COLORS[idx % AGE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number | undefined) =>
                    `${value} pacientes`
                  }
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h4>Nuevos vs Recurrentes</h4>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={firstRecurrentData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  label
                >
                  {firstRecurrentData.map((entry, idx) => (
                    <Cell
                      key={`fr-${idx}`}
                      fill={PIE_COLORS[idx % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number | undefined) =>
                    `${value} pacientes`
                  }
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="stats-section">
        <h3>Pacientes Más Activos</h3>
        <div className="top-activo-container">
          <h4 className="top-activo-heading">Top activos</h4>

          {topActivePatients && topActivePatients.length ? (
            <div className="top-activo-cards-grid">
              {topActivePatients.slice(0, 20).map((p, idx) => (
                <div key={idx} className="top-activo-card">
                  {/* Patient Rank/Number */}
                  <div className="top-activo-rank">{idx + 1}</div>

                  <div className="top-activo-info">
                    {/* 💡 REPLACED <p> with <span> for better flow */}
                    <span className="top-activo-name">{p.patientName}</span>
                    <p className="top-activo-count">
                      <span>{p.appointmentCount}</span> citas
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="top-activo-empty">
              No hay pacientes activos en el periodo.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientsStats;
